import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import MainURL from '../../../MainURL';
import CopyTextButton from '../../../components/CopyTextButton';
import Loading from '../../../components/Loading';

export type PaymentListItem = {
  id: number;
  paymentKind: 'billing' | 'oneTime';
  serviceType: string;
  userAccount: string | null;
  churchName: string | null;
  passwd: string | null;
  ownerpw: string | null;
  ordererName: string | null;
  ordererPhone: string | null;
  orderTitle: string | null;
  orderName: string | null;
  supplyAmount?: number | null;
  vatAmount?: number | null;
  totalAmount: number | null;
  portonePaymentId: string | null;
  portoneTxId?: string | null;
  plan: string | null;
  paymentStatus: string | null;
  resourceType: string | null;
  resourceId: string | null;
  createdAt: string;
};

const SERVICE_TYPE_LABELS: Record<string, string> = {
  bookletNotice: '모바일교회전단지',
  bookletEvent: '모바일행사전단지',
  bookletRetreat: '수련회 전단지',
  homeinapp: '홈인앱',
  churchapp: '교회앱',
};

const PAYMENT_KIND_LABELS: Record<string, string> = {
  billing: '정기결제',
  oneTime: '단편결제',
};

function formatAmount(amount: number | null | undefined) {
  if (amount == null || Number.isNaN(Number(amount))) return '-';
  if (Number(amount) === 0) return '무료';
  return `${Number(amount).toLocaleString('ko-KR')}원`;
}

function formatDate(value: string | null | undefined) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function serviceLabel(serviceType: string) {
  return SERVICE_TYPE_LABELS[serviceType] || serviceType || '서비스';
}

function paymentKindLabel(kind: string) {
  return PAYMENT_KIND_LABELS[kind] || kind || '-';
}

function paymentStatusLabel(status: string | null | undefined) {
  const s = String(status || '').trim().toUpperCase();
  if (s === 'PAID') return '결제완료';
  if (s === 'FREE') return '무료신청';
  if (s === 'PENDING') return '결제대기';
  if (s === 'FAILED') return '결제실패';
  return status || '-';
}

type Props = {
  userAccount: string;
};

export default function ServicePaymentList({ userAccount }: Props) {
  const [rows, setRows] = useState<PaymentListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPayments = useCallback(async () => {
    const account = userAccount.trim();
    if (!account) {
      setRows([]);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${MainURL}/payment/my/list`, {
        params: { userAccount: account, limit: 100 },
      });
      if (res.data?.ok && Array.isArray(res.data.rows)) {
        setRows(res.data.rows);
      } else {
        setRows([]);
        setError(res.data?.message || '결제 목록을 불러오지 못했습니다.');
      }
    } catch (e) {
      setRows([]);
      setError('결제 목록을 불러오지 못했습니다.');
      console.error('결제 목록 조회 실패:', e);
    } finally {
      setLoading(false);
    }
  }, [userAccount]);

  useEffect(() => {
    void fetchPayments();
  }, [fetchPayments]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="noPosts">
        <p>{error}</p>
        <button type="button" className="service-manage__retry-btn" onClick={() => void fetchPayments()}>
          다시 시도
        </button>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="noPosts">
        <p>결제한 서비스가 없습니다.</p>
      </div>
    );
  }

  return (
    <div className="mypage-scrap-list service-payment-list">
      {rows.map((item) => {
        const displayTitle =
          item.orderTitle?.trim() ||
          item.churchName?.trim() ||
          item.orderName?.trim() ||
          serviceLabel(item.serviceType);

        return (
          <article key={`${item.paymentKind}-${item.id}`} className="mypage-scrap-item">
            <div className="mypage-row-between">
              <div className="mypage-tag-row">
                <span className="mypage-tag mypage-tag--sky">{serviceLabel(item.serviceType)}</span>
                <span className="mypage-tag mypage-tag--gold">{paymentKindLabel(item.paymentKind)}</span>
              </div>
              <span className="mypage-meta"><span>{formatDate(item.createdAt)}</span></span>
            </div>
            <h4 className="mypage-scrap-title">{displayTitle}</h4>
            <div className="mypage-service-grid">
              <div className="mypage-kv">
                <div className="mypage-kv-label">결제금액</div>
                <div className="mypage-kv-value">{formatAmount(item.totalAmount)}</div>
              </div>
              <div className="mypage-kv">
                <div className="mypage-kv-label">결제상태</div>
                <div className="mypage-kv-value">{paymentStatusLabel(item.paymentStatus)}</div>
              </div>
              {item.supplyAmount != null ? (
                <div className="mypage-kv">
                  <div className="mypage-kv-label">공급가액</div>
                  <div className="mypage-kv-value">{formatAmount(item.supplyAmount)}</div>
                </div>
              ) : null}
              {item.vatAmount != null ? (
                <div className="mypage-kv">
                  <div className="mypage-kv-label">부가세</div>
                  <div className="mypage-kv-value">{formatAmount(item.vatAmount)}</div>
                </div>
              ) : null}
              {item.paymentKind === 'billing' && item.plan ? (
                <div className="mypage-kv">
                  <div className="mypage-kv-label">플랜</div>
                  <div className="mypage-kv-value">{item.plan}</div>
                </div>
              ) : null}
              {item.churchName ? (
                <div className="mypage-kv">
                  <div className="mypage-kv-label">교회명</div>
                  <div className="mypage-kv-value">{item.churchName}</div>
                </div>
              ) : null}
              {item.passwd ? (
                <div className="mypage-kv">
                  <div className="mypage-kv-label">비밀번호</div>
                  <div className="mypage-kv-value service-payment-list__mono">{item.passwd}</div>
                  <CopyTextButton text={item.passwd} className="mypage-copy-btn" />
                </div>
              ) : null}
              {item.ownerpw ? (
                <div className="mypage-kv">
                  <div className="mypage-kv-label">관리자 비밀번호</div>
                  <div className="mypage-kv-value service-payment-list__mono">{item.ownerpw}</div>
                  <CopyTextButton text={item.ownerpw} className="mypage-copy-btn" />
                </div>
              ) : null}
              {item.ordererName ? (
                <div className="mypage-kv">
                  <div className="mypage-kv-label">주문자</div>
                  <div className="mypage-kv-value">{item.ordererName}</div>
                </div>
              ) : null}
              {item.ordererPhone ? (
                <div className="mypage-kv">
                  <div className="mypage-kv-label">연락처</div>
                  <div className="mypage-kv-value">{item.ordererPhone}</div>
                </div>
              ) : null}
              {item.portonePaymentId ? (
                <div className="mypage-kv" style={{ gridColumn: '1 / -1' }}>
                  <div className="mypage-kv-label">결제 ID</div>
                  <div className="mypage-kv-value service-payment-list__mono">{item.portonePaymentId}</div>
                </div>
              ) : null}
            </div>
            {item.ownerpw ? (
              <p className="service-payment-list__ownerpw-warn">관리자 비번은 공유하지 마세요.</p>
            ) : null}
          </article>
        );
      })}
    </div>
  );
}
